/**
 * PillarLayout — Shell component combining the pillar nav, secondary nav,
 * and the page content slot.
 *
 * Used when the `pillarNav` feature flag is enabled. Replaces the old
 * dropdown-based navigation with a two-tier tab system.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SearchModal from '../search/SearchModal';
import EmailVerificationBanner from '../EmailVerificationBanner';
import SiteBanner from '../SiteBanner';
import { QuickPraiseModal } from '../praise';
import PillarNav from './PillarNav';
import AccountSwitcher from '../AccountSwitcher';
import { NavQuickSwitch } from '../AccountSwitcher';
import MobileOrgSwitcher from './MobileOrgSwitcher';
import SecondaryNav from './SecondaryNav';
import PillarTooltip from './PillarTooltip';
import HierarchyChip from './HierarchyChip';
import HierarchySwitcherSheet from './HierarchySwitcherSheet';
import MultiHierarchyHint from './MultiHierarchyHint';
import { getActivePillar, shouldHideBottomNav } from './navConfig';
import ActivePopBanner from '../Task/ActivePopBanner';
import GracePeriodBannerConnected from '../GracePeriodBannerConnected';
import RoleToggle from '../nav/RoleToggle';
import { QuickActionsProvider } from '../quick-actions';
import { useActiveRole } from '../../hooks/useActiveRole';
import { useLayout } from './useLayout';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useDefaultPillar } from '../../hooks/useDefaultPillar';
import { useFeatureTracking } from '../../hooks/useFeatureTracking';
import { MobileBottomNav } from './MobileBottomNav';
import { getDisplayName, getInitials } from '../../utils/displayName';
import { Button, IconButton, Card } from '../ui';
import { isDomRole } from '../../utils/roleHelpers';

interface PillarLayoutProps {
  children: React.ReactNode;
  /**
   * Hide the MobileBottomNav so a detail view can mount the fixed 64px action
   * bar (anatomy contract §3.7 / A9). Defaults to the per-route flag in
   * navConfig's HIDE_BOTTOM_NAV_ROUTES; pass explicitly to override.
   */
  hideBottomNav?: boolean;
}

const PillarLayout: React.FC<PillarLayoutProps> = ({ children, hideBottomNav }) => {
  useFeatureTracking();
  const layout = useLayout();
  const { organizations, selectOrganization } = useOrganization();
  const { pathname } = useLocation();
  const { defaultPillar } = useDefaultPillar();
  const { isSwitch, effectiveRole, toggleRole } = useActiveRole(
    layout.selectedOrg ? String(layout.selectedOrg.id) : null,
    layout.user?.id
  );
  const routePillar = getActivePillar(pathname);
  const bottomNavHidden = hideBottomNav ?? shouldHideBottomNav(pathname);
  const [searchOpen, setSearchOpen] = useState(false);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  // Spec 287: mobile Hierarchy switcher sheet, opened from the header chip.
  const [hierarchySheetOpen, setHierarchySheetOpen] = useState(false);
  const displayName = getDisplayName(layout.user ?? null);
  const userInitial = getInitials(displayName);

  // Cmd+K / Ctrl+K to open search
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Keep --navbar-height in sync with the actual rendered navbar height so
  // SecondaryNav's sticky top offset is always correct regardless of content changes.
  const navbarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = navbarRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const height = entries[0]?.borderBoxSize?.[0]?.blockSize ?? entries[0]?.contentRect.height;
      if (height != null) {
        document.documentElement.style.setProperty('--navbar-height', `${Math.round(height)}px`);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // On the Helm dashboard, show the user's preferred pillar's secondary nav
  const isHelmPage = pathname === '/helm' || pathname === '/';
  const activePillar = routePillar || (isHelmPage ? defaultPillar : null);

  return (
    <QuickActionsProvider>
    <div className="layout layout--v2 min-h-screen flex flex-col bg-[var(--theme-bg-primary)]">
      {/* Top navbar — brand + hamburger + org/user controls */}
      <nav ref={navbarRef} className="navbar bg-[var(--theme-bg-elevated)] md:shadow-sm sticky top-0 z-[100]" role="navigation" aria-label="Main navigation">
        <div className="max-w-[1400px] mx-auto px-3 py-1.5 flex justify-start items-center gap-6 sm:px-4 md:px-6 md:py-2">
          {/* Brand mark — desktop: always visible; mobile: shown via role-specific div below */}
          <div className="hidden md:flex items-center shrink-0">
            <Link to="/helm" className="flex items-center gap-2 text-xl font-bold text-[var(--theme-primary)] no-underline tracking-tight">Helm</Link>
          </div>

          {/* Mobile: Helm wordmark + Hierarchy chip (Spec 287) + avatar button (full-width row) */}
          <div className="flex items-center justify-between w-full md:hidden">
            <div className="flex items-center gap-2 min-w-0">
              <Link to="/helm" className="text-base font-bold text-[var(--theme-primary)] no-underline sm:text-lg shrink-0">Helm</Link>
              <HierarchyChip open={hierarchySheetOpen} onOpen={() => setHierarchySheetOpen(true)} />
            </div>
            <button
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white border-none cursor-pointer shrink-0"
              style={{ background: 'var(--theme-cta-gradient)' }}
              onClick={() => layout.setMobileMenuOpen(!layout.mobileMenuState.mobileMenuOpen)}
              aria-expanded={layout.mobileMenuState.mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={layout.mobileMenuState.mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {userInitial}
            </button>
          </div>

          {/* Pillar tabs in top bar — desktop only (PillarNav has hidden md:flex); mobile uses bottom nav */}
          <PillarNav overridePillarId={isHelmPage ? defaultPillar : undefined} />

          {/* Desktop: Org dropdown + User dropdown (right side) */}
          <div className="hidden md:flex items-center gap-2 ml-auto">
            <IconButton
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              }
              aria-label="Search (Cmd+K)"
              title="Search (Cmd+K)"
              variant="ghost"
              size="md"
              onClick={() => setSearchOpen(true)}
            />
            <RoleToggle isSwitch={isSwitch} effectiveRole={effectiveRole} onToggle={toggleRole} />
            <NavQuickSwitch />
            {/* 2026-07-05: was missing the 'owner' org role (a real value,
                e.g. solo age-verification accounts). */}
            {layout.selectedOrg && isDomRole(layout.selectedOrg.role, layout.user?.role) && (
              <Button
                variant="secondary"
                onClick={() => layout.setPraiseModalOpen(true)}
              >
                Send Praise
              </Button>
            )}
            {layout.selectedOrg && layout.selectedOrg.subscriptionTier === 'free' && (
              <Link to="/billing" className="px-3.5 py-2 bg-transparent border border-accent rounded-lg text-accent font-medium text-sm no-underline transition-all hover:bg-accent hover:text-[var(--theme-text-inverse)]">Upgrade</Link>
            )}
            {/* Org dropdown */}
            <div className="relative" ref={layout.dropdownRefs.orgDropdownRef}>
              <button
                data-testid="org-menu-button"
                className="flex items-center gap-1.5 px-3 py-2 bg-transparent border border-border-light rounded-lg cursor-pointer text-text-secondary font-medium text-sm transition-all hover:bg-[var(--theme-bg-tertiary)] hover:border-[var(--theme-border-medium)] hover:text-text-primary"
                onClick={() => layout.openDropdown('orgDropdownOpen')}
                aria-expanded={layout.dropdownState.orgDropdownOpen}
                aria-haspopup="true"
              >
                <span className="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap">{layout.selectedOrg?.name || 'Select Hierarchy'}</span>
                <span className="text-[10px] text-text-secondary">&#9662;</span>
              </button>
              {layout.dropdownState.orgDropdownOpen && (
                <Card data-testid="org-dropdown" tone="elevated" radius="lg" padding="none" className="absolute top-full right-0 mt-1 min-w-[200px] border border-border-light shadow-lg z-[200] overflow-hidden">
                  {/* Active org + settings */}
                  <div data-testid="org-dropdown-active-section" className="py-1 border-b border-border-light">
                    <div className="flex items-center justify-between px-3.5 py-2">
                      <span data-testid="org-dropdown-active-name" className="text-sm font-medium text-text-primary truncate">{layout.selectedOrg?.name}</span>
                      <span className="text-[10px] font-semibold uppercase text-success shrink-0 ml-2">Active</span>
                    </div>
                    {/* 2026-07-05: was `role === 'admin' || role === 'super_admin'` —
                        the second branch checked org role against a value it can
                        never hold ('super_admin' is exclusively the platform
                        user.role); the real, previously-missed gap was 'owner'. */}
                    {layout.selectedOrg && isDomRole(layout.selectedOrg.role, layout.user?.role) && (
                      <Link data-testid="org-dropdown-settings" to="/organizations/settings" className="block w-full py-2 px-3.5 text-left bg-none border-none text-text-secondary text-sm cursor-pointer no-underline transition-colors hover:bg-[var(--theme-bg-tertiary)]" onClick={() => layout.setOrgDropdownOpen(false)}>
                        Hierarchy Settings
                      </Link>
                    )}
                  </div>
                  {/* Other orgs to switch to */}
                  {organizations.length > 1 && (
                    <div data-testid="org-dropdown-others" className="py-1 border-b border-border-light">
                      {organizations.filter(org => org.id !== layout.selectedOrg?.id).map((org) => (
                        <button
                          key={org.id}
                          className="flex items-center justify-between w-full py-2 px-3.5 bg-transparent border-none text-sm cursor-pointer text-left text-text-secondary transition-colors hover:bg-[var(--theme-bg-tertiary)]"
                          onClick={() => { selectOrganization(org); layout.setOrgDropdownOpen(false); }}
                          type="button"
                        >
                          <span className="truncate">{org.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Explore / Manage */}
                  {organizations.length === 1 ? (
                    <Link data-testid="org-dropdown-explore" to="/cultivate/hierarchies" className="block w-full py-2.5 px-3.5 text-left bg-none border-none text-text-secondary text-sm cursor-pointer no-underline transition-colors hover:bg-[var(--theme-bg-tertiary)]" onClick={() => layout.setOrgDropdownOpen(false)}>
                      Explore Hierarchies
                    </Link>
                  ) : (
                    <Link data-testid="org-dropdown-manage" to="/organizations" className="block w-full py-2.5 px-3.5 text-left bg-none border-none text-text-secondary text-sm cursor-pointer no-underline transition-colors hover:bg-[var(--theme-bg-tertiary)]" onClick={() => layout.setOrgDropdownOpen(false)}>
                      Manage Hierarchies
                    </Link>
                  )}
                </Card>
              )}
            </div>
            {/* User dropdown */}
            <div className="relative" ref={layout.dropdownRefs.userDropdownRef}>
              <button
                className="flex items-center gap-1.5 px-3 py-2 bg-transparent border border-border-light rounded-lg cursor-pointer text-text-secondary font-medium text-sm transition-all hover:bg-[var(--theme-bg-tertiary)] hover:border-[var(--theme-border-medium)] hover:text-text-primary"
                onClick={() => layout.openDropdown('userDropdownOpen')}
                aria-expanded={layout.dropdownState.userDropdownOpen}
                aria-haspopup="true"
                data-testid="user-menu-button"
              >
                <span className="max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">{getDisplayName(layout.user ?? null)}</span>
                <span className="text-[10px] text-text-secondary">&#9662;</span>
              </button>
              {layout.dropdownState.userDropdownOpen && (
                <Card tone="elevated" radius="lg" padding="none" className="absolute top-full right-0 mt-1 min-w-[180px] border border-border-light shadow-lg z-[200] overflow-hidden">
                  {/* Contract §9: Safeword is pillar-independent — fixed first slot in the
                      avatar menu on every screen, never behind More. */}
                  <Link to="/safeword" className="block w-full py-2.5 px-3.5 text-left bg-none border-none text-text-primary text-sm cursor-pointer no-underline transition-colors hover:bg-[var(--theme-bg-tertiary)]" onClick={() => layout.setUserDropdownOpen(false)}>
                    Safeword
                  </Link>
                  {layout.user?.role === 'super_admin' && (
                    <Link to="/admin" className="block w-full py-2.5 px-3.5 text-left bg-none border-none text-text-primary text-sm cursor-pointer no-underline transition-colors hover:bg-[var(--theme-bg-tertiary)]" onClick={() => layout.setUserDropdownOpen(false)}>
                      Platform Admin
                    </Link>
                  )}
                  <Link to="/settings" className="block w-full py-2.5 px-3.5 text-left bg-none border-none text-text-primary text-sm cursor-pointer no-underline transition-colors hover:bg-[var(--theme-bg-tertiary)]" onClick={() => layout.setUserDropdownOpen(false)}>
                    Account Settings
                  </Link>
                  <button
                    className="block w-full py-2.5 px-3.5 text-left bg-none border-none text-[var(--theme-error)] text-sm cursor-pointer transition-colors hover:bg-[rgba(244,67,54,0.1)]"
                    onClick={() => { layout.setUserDropdownOpen(false); layout.handleLogout(); }}
                  >
                    Logout
                  </button>
                </Card>
              )}
            </div>
          </div>

          {/* Mobile slide-out — org/account/logout only (pillar tabs handle feature nav) */}
          <div
            className={`flex flex-col fixed top-0 w-[260px] sm:w-[280px] h-screen bg-[var(--theme-bg-elevated)] pt-[72px] px-5 pb-6 shadow-[-4px_0_20px_rgba(0,0,0,0.15)] transition-[right] duration-300 ease-in-out z-[100] overflow-y-auto md:hidden ${
              layout.mobileMenuState.mobileMenuOpen ? 'right-0' : 'right-[-260px] sm:right-[-280px]'
            }`}
            id="mobile-menu"
          >
            {/* Contract §9: Safeword is pillar-independent — fixed FIRST slot of the
                avatar menu on every screen, never behind More, never below the fold. */}
            <div className="py-4 border-b border-border-light flex flex-col gap-1">
              <Link to="/safeword" className="block w-full py-3 px-4 text-[15px] font-semibold text-text-secondary no-underline rounded-lg transition-colors hover:bg-[var(--theme-bg-tertiary)] hover:text-text-primary" onClick={() => layout.setMobileMenuOpen(false)}>
                Safeword
              </Link>
            </div>
            <div className="py-4 border-b border-border-light flex flex-col gap-1">
              <MobileOrgSwitcher onSwitch={() => layout.setMobileMenuOpen(false)} />
              <AccountSwitcher />
              {isSwitch && (
                <div>
                  <RoleToggle isSwitch={isSwitch} effectiveRole={effectiveRole} onToggle={toggleRole} />
                </div>
              )}
            </div>
            <div className="py-4 border-b border-border-light flex flex-col gap-1">
              <button
                className="block w-full py-3 px-4 text-[15px] font-semibold text-text-secondary no-underline rounded-lg bg-none border-none text-left cursor-pointer transition-colors hover:bg-[var(--theme-bg-tertiary)] hover:text-text-primary"
                onClick={() => { setSearchOpen(true); layout.setMobileMenuOpen(false); }}
              >
                Search
              </button>
              {/* 2026-07-05: was missing the 'owner' org role (a real value,
                  e.g. solo age-verification accounts). */}
              {layout.selectedOrg && isDomRole(layout.selectedOrg.role, layout.user?.role) && (
                <button
                  className="block w-full py-3 px-4 text-[15px] font-semibold text-text-secondary no-underline rounded-lg bg-none border-none text-left cursor-pointer transition-colors hover:bg-[var(--theme-bg-tertiary)] hover:text-text-primary"
                  onClick={() => { layout.setPraiseModalOpen(true); layout.setMobileMenuOpen(false); }}
                >
                  Send Praise
                </button>
              )}
              {/* 2026-07-05: was `role === 'admin' || role === 'super_admin'` —
                  the second branch checked org role against a value it can
                  never hold ('super_admin' is exclusively the platform
                  user.role); the real, previously-missed gap was 'owner'. */}
              {layout.selectedOrg && isDomRole(layout.selectedOrg.role, layout.user?.role) && (
                <Link to="/organizations/settings" className="block w-full py-3 px-4 text-[15px] font-semibold text-text-secondary no-underline rounded-lg transition-colors hover:bg-[var(--theme-bg-tertiary)] hover:text-text-primary" onClick={() => layout.setMobileMenuOpen(false)}>
                  Hierarchy Settings
                </Link>
              )}
              {organizations.length === 1 && (
                <Link to="/cultivate/hierarchies" className="block w-full py-3 px-4 text-[15px] font-semibold text-text-secondary no-underline rounded-lg transition-colors hover:bg-[var(--theme-bg-tertiary)] hover:text-text-primary" onClick={() => layout.setMobileMenuOpen(false)}>
                  Explore Hierarchies
                </Link>
              )}
            </div>
            <div className="py-4 flex flex-col gap-1">
              {layout.user?.role === 'super_admin' && (
                <Link to="/admin" className="block w-full py-3 px-4 text-[15px] font-semibold text-text-secondary no-underline rounded-lg transition-colors hover:bg-[var(--theme-bg-tertiary)] hover:text-text-primary" onClick={() => layout.setMobileMenuOpen(false)}>
                  Platform Admin
                </Link>
              )}
              <Link to="/settings" className="block w-full py-3 px-4 text-[15px] font-semibold text-text-secondary no-underline rounded-lg transition-colors hover:bg-[var(--theme-bg-tertiary)] hover:text-text-primary" onClick={() => layout.setMobileMenuOpen(false)}>
                Account Settings
              </Link>
              <button
                className="block w-full py-3 px-4 text-[15px] font-semibold text-[var(--theme-error)] no-underline rounded-lg bg-none border-none text-left cursor-pointer transition-colors hover:bg-[rgba(220,53,69,0.1)]"
                onClick={() => { layout.setMobileMenuOpen(false); layout.handleLogout(); }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>

      </nav>

      {layout.mobileMenuState.mobileMenuOpen && (
        <div
          className="block fixed inset-0 bg-black/50 z-[99] md:hidden"
          onClick={() => layout.setMobileMenuOpen(false)}
        />
      )}

      {/* Spec 287: one-time hint pointing at the header Hierarchy chip */}
      <MultiHierarchyHint />

      {/* Secondary tabs (pillar-specific) */}
      {activePillar && <SecondaryNav overridePillarId={isHelmPage ? defaultPillar : undefined} />}

      {/* Spec 163: First-visit pillar tooltip (below SecondaryNav, above content) */}
      {activePillar && <PillarTooltip overridePillarId={isHelmPage ? defaultPillar : undefined} />}

      <SiteBanner />
      <GracePeriodBannerConnected />

      {layout.user && !layout.user.emailVerified && (
        <EmailVerificationBanner email={layout.user.email} />
      )}

      <ActivePopBanner showTaskTitle />

      <main className="main-content flex-1 max-w-[1400px] w-full mx-auto px-3 py-4 sm:p-6 pb-20 md:pb-6" role="main">{children}</main>

      {layout.selectedOrg && (
        <QuickPraiseModal
          isOpen={layout.praiseModalOpen}
          onClose={() => layout.setPraiseModalOpen(false)}
          organizationId={String(layout.selectedOrg.id)}
        />
      )}

      <footer className="mt-auto p-4 text-center border-t border-border-light bg-[var(--theme-bg-secondary)]" role="contentinfo">
        <div className="flex justify-center items-center gap-2 text-xs">
          <Link to="/support" className="text-text-tertiary no-underline transition-colors hover:text-text-primary">Help</Link>
          <span className="text-text-tertiary">&middot;</span>
          <Link to="/privacy-policy" className="text-text-tertiary no-underline transition-colors hover:text-text-primary">Privacy</Link>
          <span className="text-text-tertiary">&middot;</span>
          <Link to="/terms-of-service" className="text-text-tertiary no-underline transition-colors hover:text-text-primary">Terms</Link>
        </div>
      </footer>

      {!bottomNavHidden && <MobileBottomNav />}
      <SearchModal isOpen={searchOpen} onClose={closeSearch} />
      <HierarchySwitcherSheet
        open={hierarchySheetOpen}
        onDismiss={() => setHierarchySheetOpen(false)}
      />
    </div>
    </QuickActionsProvider>
  );
};

export default PillarLayout;
